<template>
  <div id="orders-by-month">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          <template v-if="showDateTypeSelector">
            {{ $t(`views.dashboardDetail.orders.ordersByMonth.title.${type}`) }}
            <v-chip-group v-model="dateType" active-class="primary" mandatory column class="ml-3">
              <v-chip filter value="month" small class="mx-0">
                {{ $t('common.month') }}
              </v-chip>
              <span class="mx-1">/</span>
              <v-chip filter value="year" small class="mx-0">
                {{ $t('common.year') }}
              </v-chip>
            </v-chip-group>
          </template>
          <template v-else>
            {{ $t(`views.dashboardDetail.orders.ordersByMonth.title`) }}
            {{ $t(`common.${dateType}`) }}
          </template>
        </h1>
        <v-spacer />
        <ShowCashButton
          v-show="onlyCashOrdersTypes.indexOf(type) === -1 && !filters.positionType"
          class="mt-1"
          :showCash="showCash"
          @showCashChanged="showCashChanged"
        />
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="loadedData"
          ref="barChart"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="orders-by-month-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <template v-if="type === 'scrip'">
                  <td class="text-right">
                    <currency :value="totalRow.positionNumber" />
                  </td>
                </template>
                <template v-else>
                  <td class="text-right">
                    <currency :value="totalRow.totalBaseCurrency" :currency="user.baseCurrency" />
                  </td>
                  <template v-if="onlyCashOrdersTypes.indexOf(type) === -1">
                    <td class="text-right">
                      <currency
                        :value="totalRow.totalWithComissionBaseCurrency"
                        :currency="user.baseCurrency"
                        strong
                      />
                    </td>
                  </template>
                </template>
              </tr>
            </template>
            <template v-slot:item._id="{ item }">
              <div class="items-flex">
                <span>
                  <template v-if="dateType === 'month'">
                    <strong>{{ $t(`common.months.${item._id.month}`) }} {{ item._id.year }}</strong>
                  </template>
                  <template v-else>
                    <strong>{{ item._id }}</strong>
                  </template>
                </span>
                <template v-if="someNullExchangeRate(item)">
                  <div class="ml-3">
                    <WarningIcon :text="$t(`views.dashboardDetail.orders.ordersByMonth.warning`)" />
                  </div>
                </template>
              </div>
            </template>
            <template v-slot:item.positionNumber="{ item }">
              <currency :value="item.positionNumber" :decimals="7" />
            </template>
            <template v-slot:item.totalBaseCurrency="{ item }">
              <currency :value="item.totalBaseCurrency" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalWithComissionBaseCurrency="{ item }">
              <currency
                :value="item.totalWithComissionBaseCurrency"
                :currency="user.baseCurrency"
                strong
              />
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map, get, find } from 'lodash'
import { mapState } from 'vuex'
import { getOrderSummary, getScripSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'
import WarningIcon from '@/views/components/common/WarningIcon.vue'
import ShowCashButton from '@/views/components/common/ShowCashButton'

export default {
  components: { Currency, BarChart, BarLoading, WarningIcon, ShowCashButton },
  props: {
    type: {
      type: String,
      required: true,
    },
    showTable: {
      type: Boolean,
      default: true,
    },
    showDateTypeSelector: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    summary: null,
    mySummary: [],
    totalRow: null,
    currentPage: null,
    loadedData: false,
    dateType: 'month',
    barChartData: {
      labels: null,
      datasets: null,
    },
    filters: [],
    showCash: false,
    onlyCashOrdersTypes: ['interest', 'deposit', 'withdrawal'],
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      const isScrip = this.type === 'scrip'
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
            },
          ],
          yAxes: [
            {
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: !isScrip ? context.user.baseCurrency : undefined,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: !isScrip ? context.user.baseCurrency : undefined,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      const headers = [
        {
          text:
            this.dateType === 'month'
              ? this.$t('views.orders.ordersTable.month')
              : this.$t('views.orders.ordersTable.year'),
          value: '_id',
          sortable: false,
          divider: true,
        },
      ]
      if (this.type === 'scrip') {
        headers.push({
          text: `${this.$t('views.orders.ordersTable.positionNumber')}`,
          value: 'positionNumber',
          align: 'right',
        })
      } else {
        headers.push({
          text: `${this.$t('views.orders.ordersTable.total')} (${getCurrencySymbol(
            this.user.baseCurrency
          )})`,
          value: 'totalBaseCurrency',
          align: 'right',
        })
        if (this.onlyCashOrdersTypes.indexOf(this.type) === -1) {
          headers.push({
            text: `${this.$t('views.orders.ordersTable.totalWithComission')} (${getCurrencySymbol(
              this.user.baseCurrency
            )})`,
            value: 'totalWithComissionBaseCurrency',
            align: 'right',
          })
        }
      }
      return headers
    },
  },
  watch: {
    dateType: {
      handler() {
        this.changeType()
      },
    },
  },
  methods: {
    someNullExchangeRate(item) {
      return item.exchangeRates && item.exchangeRates.some((elem) => elem === null)
    },
    itemRowBackground(item) {
      if (this.someNullExchangeRate(item)) return 'row-advise'
    },
    createBarChartObject() {
      const summary = this.mySummary.slice().reverse()

      this.barChartData.datasets = [
        {
          label:
            this.type === 'scrip'
              ? `${this.$t('views.orders.ordersTable.positionNumber')}`
              : `${this.$t('views.orders.ordersTable.totalWithComission')} (${
                  this.user.baseCurrency
                })`,
          data:
            this.type === 'scrip'
              ? map(summary, 'positionNumber')
              : map(summary, 'totalWithComissionBaseCurrency'),
          backgroundColor: {
            buy: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarBlue
              : DEFAULT_PALETTE.chartsBarBlue,
            sell: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarRed
              : DEFAULT_PALETTE.chartsBarRed,
            scrip: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarBlue
              : DEFAULT_PALETTE.chartsBarBlue,
            interest: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen,
            deposit: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarGreen
              : DEFAULT_PALETTE.chartsBarGreen,
            withdrawal: this.$vuetify.theme.dark
              ? DARK_DEFAULT_PALETTE.chartsBarRed
              : DEFAULT_PALETTE.chartsBarRed,
          }[this.type],
        },
      ]

      let labels
      if (this.dateType === 'month') {
        labels = map(
          summary,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summary, (item) => item._id)
      }
      this.barChartData.labels = labels

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    changeType() {
      let summary = get(find(this.summary, { type: this.type }), 'years', [])
      summary = JSON.parse(JSON.stringify(summary))

      if (summary.length && this.dateType === 'month') {
        let summaryMonths = [summary[0]]
        summary.forEach((year) => {
          if (year.months) summaryMonths = summaryMonths.concat(year.months)
        })
        this.totalRow = summaryMonths.shift()
        this.mySummary = summaryMonths
      } else {
        this.totalRow = summary.shift()
        this.mySummary = summary
      }
      this.currentPage = 1

      this.createBarChartObject()
    },
    async loadSummary(filters) {
      this.loadedData = false
      this.filters = filters
      try {
        let summary
        if (this.type === 'scrip') {
          summary = await getScripSummary({
            group: 'byMonth',
            filter: {
              ...filters,
            },
          })
        } else if (this.onlyCashOrdersTypes.indexOf(this.type) > -1) {
          summary = await getOrderSummary({
            group: 'byMonth',
            filter: {
              type: this.type,
              ...filters,
            },
          })
        } else {
          summary = await getOrderSummary({
            group: 'byMonth',
            filter: {
              type: this.type,
              positionType: !this.showCash ? { $ne: 'cash' } : undefined,
              isFxOrder: { $ne: true },
              ...filters,
            },
          })
        }
        this.summary = summary.data

        const isAllDate = !filters.date || Object.keys(filters.date).length === 0
        if (isAllDate) {
          this.$set(this, 'dateType', 'year')
        } else {
          this.$set(this, 'dateType', 'month')
        }

        this.changeType()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
    showCashChanged(showCash) {
      this.showCash = showCash
      this.loadSummary(this.filters)
    },
  },
}
</script>

<style lang="scss">
#orders-by-month {
  .card-title {
    display: flex;
    align-items: center;
  }

  #orders-by-month-table {
    .items-flex {
      display: flex;
    }
  }
}
</style>
